<template>
  <div>
    <SnackbarComponent
      v-model="snackbar.show"
      :message="snackbar.message"
      :color="snackbar.color"
    />
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 pa-4"> Edit Medication Class </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid">
            <div class="mb-4">
              <v-text-field
                v-model="formData.class_name"
                :rules="[(v) => !!v || 'Class Name is required']"
                label="Class Name"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
              />
              <v-select
                v-model="formData.medication_category_id"
                :items="categories"
                :item-title="(c) => formatMedicationCategoryName(c.medication_category_name)"
                item-value="medication_category_id"
                :rules="[(v) => !!v || 'Category is required']"
                label="Category"
                variant="outlined"
                density="compact"
                class="mb-2"
                required
              />
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="handleClose">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="!valid || loading"
            :loading="loading"
            @click="handleSubmit"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { edit_master_medication_class } from "@/graphql/mutations";
import GetMedicationCategories from "@/mixins/Medication/GetMedicationCategories";
import { formatMedicationCategoryName } from "@/utils/medicationCategoryLabel";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

export default {
  name: "EditSubCategoryDialog",
  mixins: [GetMedicationCategories],
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    subCategory: {
      type: Object,
      default: null,
    },
  },
  emits: ["update:modelValue", "updated"],
  data() {
    return {
      valid: false,
      loading: false,
      categories: [],
      formData: {
        class_name: "",
        medication_category_id: null,
      },
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.loadCategories().then(() => {
          if (this.subCategory) {
            this.loadMedicationClassData();
          }
        });
      } else {
        this.resetForm();
      }
    },
    subCategory: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.modelValue) {
          this.loadMedicationClassData();
        }
      },
    },
  },
  methods: {
    formatMedicationCategoryName,
    async loadCategories() {
      try {
        await this.fetchMedicationCategories();
        this.categories = this.medicationCategoriesList.map((item) => ({
          medication_category_id: item.medication_category_id,
          medication_category_name: item.medication_category_name,
        }));
      } catch (error) {
        console.error("Error loading medication categories:", error);
      }
    },
    loadMedicationClassData() {
      if (!this.subCategory) {
        return;
      }

      const matchedCategory =
        this.categories.find(
          (c) =>
            String(c.medication_category_id) ===
            String(this.subCategory.medication_category_id)
        ) ||
        this.categories.find(
          (c) =>
            (c.medication_category_name || "").toLowerCase() ===
            (this.subCategory.medication_category_name || "").toLowerCase()
        );

      this.formData = {
        class_name: this.subCategory.class_name || "",
        medication_category_id:
          matchedCategory?.medication_category_id ||
          this.subCategory.medication_category_id ||
          null,
      };
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        class_name: "",
        medication_category_id: null,
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    getSelectedCategoryName() {
      const selectedCategory = this.categories.find(
        (category) =>
          String(category.medication_category_id) ===
          String(this.formData.medication_category_id)
      );
      return (
        selectedCategory?.medication_category_name ||
        this.subCategory?.medication_category_name ||
        ""
      );
    },
    async handleSubmit() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.loading = true;

      try {
        const input = {
          class_id: this.subCategory?.class_id || "",
          class_name: this.formData.class_name.trim(),
          medication_category_id: this.formData.medication_category_id,
          medication_category_name: this.getSelectedCategoryName(),
        };

        const response = await client.graphql({
          query: edit_master_medication_class,
          variables: {
            input,
          },
        });

        const responseData = JSON.parse(
          response.data.edit_master_medication_class
        );

        if (responseData.status == "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              "Medication class updated successfully",
            color: "success",
          };
          this.$emit("updated", 0);
          this.handleClose();
        } else {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              "Failed to update medication class",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error updating medication class:", error);
        this.snackbar = {
          show: true,
          message:
            error.message ||
            "An error occurred while updating the medication class",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
